/**
 * Weekly hours: a per-weekday view over availability rules, plus the pure planning functions the
 * weekly hours panel runs. A rule covers several weekdays with one window; the panel shows and edits
 * a weekday at a time, so every change is expressed as the rule operations it needs.
 */

import type { AvailabilityRule } from '../../../../lib/leasing.api';
import { timeToMinutes } from './time-utils';

type RuleWindowFields = Pick<AvailabilityRule, 'weekdays' | 'startTime' | 'endTime'>;

export interface DayWindow {
  ruleId: string;
  startTime: string;
  endTime: string;
}

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** The windows of each ISO weekday (1..7), earliest first. */
export function windowsByWeekday(rules: readonly (Pick<AvailabilityRule, 'id'> & RuleWindowFields)[]): Record<number, DayWindow[]> {
  const byDay: Record<number, DayWindow[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
  for (const rule of rules) {
    for (const day of rule.weekdays) {
      byDay[day]?.push({ ruleId: rule.id, startTime: rule.startTime, endTime: rule.endTime });
    }
  }
  for (const day of WEEKDAYS) byDay[day]!.sort((a, b) => a.startTime.localeCompare(b.startTime));
  return byDay;
}

/** Hours per week the landlord is available: overlapping windows on one weekday count once. */
export function weeklyHours(rules: readonly RuleWindowFields[]): number {
  let minutes = 0;
  for (const day of WEEKDAYS) {
    const windows = rules
      .filter((rule) => rule.weekdays.includes(day))
      .map((rule) => [timeToMinutes(rule.startTime), timeToMinutes(rule.endTime)] as const)
      .sort((a, b) => a[0] - b[0]);
    let coveredUntil = -1;
    for (const [start, end] of windows) {
      const from = Math.max(start, coveredUntil);
      if (end > from) minutes += end - from;
      coveredUntil = Math.max(coveredUntil, end);
    }
  }
  return Math.round(minutes / 60);
}

/** Returns a message when the window is invalid or overlaps another window that day, else null. */
export function validateWindow(
  startTime: string,
  endTime: string,
  others: readonly Pick<DayWindow, 'startTime' | 'endTime'>[]
): string | null {
  if (timeToMinutes(endTime) <= timeToMinutes(startTime)) return 'The end time must be after the start time.';
  const overlaps = others.some(
    (other) =>
      timeToMinutes(startTime) < timeToMinutes(other.endTime) && timeToMinutes(endTime) > timeToMinutes(other.startTime)
  );
  return overlaps ? 'That overlaps another window on this day.' : null;
}

export interface RuleChange {
  /** Delete the rule. */
  delete?: boolean;
  /** Replace the rule's weekdays. */
  weekdays?: number[];
}

/** What removing `day` from a rule means for the rule: delete it when it was the only day, else drop the day. */
export function ruleWithoutDay(rule: Pick<AvailabilityRule, 'weekdays'>, day: number): RuleChange {
  const remaining = rule.weekdays.filter((d) => d !== day);
  return remaining.length === 0 ? { delete: true } : { weekdays: remaining };
}

/** The weekdays Tuesday..Friday that "Same hours Monday to Friday" copies Monday's windows onto. */
export const SAME_HOURS_TARGET_DAYS: readonly number[] = [2, 3, 4, 5];
